import { useEffect, useRef, useState, useContext } from 'react';
import UserMenu from '../Elements/UserMenu';
import HomeData from './home/HomeData';
import Workers from './worker/Workers';
import RevenuePage from './revenue/RevenuePage';
import Settings from '../settings/Settings';
import Password from '../ChangePassword/Password';
import CoinDropdown from '../Elements/CoinDropDown';
import { CoinContext, CoinProvider } from '../../context/CoinContext';
import Switcher from '../auth/Switcher';
import AdminPanel from '../admin/AdminPanel';
import axios from 'axios';

interface UserInfo {
    userName: string;
    email: string;
    type: string;
}

function WorkerData() {
    const [activeTab, setActiveTab] = useState('HOME');
    const [settingsActive, setSettingsActive] = useState(false);
    const [passwordActive, setPasswordActive] = useState(false);
    const [adminPanelActive, setAdminPanelActive] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showAgreementModal, setShowAgreementModal] = useState(false);
    const [userInfoLoaded, setUserInfoLoaded] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [showAdminPanel, setShowAdminPanel] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const coinContext = useContext(CoinContext);

    if (!coinContext) {
        throw new Error('CoinContext must be used within a CoinProvider');
    }

    const { selectedCoin, setSelectedCoin } = coinContext;

    useEffect(() => {
        checkUserRole();
    }, []);

    useEffect(() => {
        checkUserRole();
    }, []);

    useEffect(() => {
        if (selectedCoin && userInfoLoaded) {
            checkAgreementStatus();
        }
    }, [selectedCoin, userInfoLoaded]);

    const checkUserRole = async () => {
        try {
            const response = await axios.get<UserInfo>('/pool/info');
            setIsAdmin(response.data.type === 'ADMIN');
            setUserInfoLoaded(true);
        } catch (error) {
            console.error('Error checking user role:', error);
            setIsAdmin(false);
            setUserInfoLoaded(true);
        }
    };

    const checkAdminStatus = async () => {
        try {
            const response = await axios.get<UserInfo>('/pool/info');
            setIsAdmin(response.data.type === 'ADMIN');
        } catch (error) {
            console.error('Error checking admin status:', error);
            setIsAdmin(false);
        }
    };

    const checkAgreementStatus = async () => {
        try {
            const response = await axios.get(`/pool/worker/agreement?coin=${selectedCoin}`);
            if (response.data.status === false) {
                setShowAgreementModal(true);
            }
        } catch (error) {
            console.error('Error checking agreement status:', error);
        }
    };

    const handleAdminPanelClick = async () => {
        try {
            const response = await axios.get<UserInfo>('/pool/info');
            if (response.data.type === 'ADMIN') {
                setAdminPanelActive(true);
                setActiveTab('ADMIN');
                setSettingsActive(false);
                setPasswordActive(false);
            } else {
                // Redirect non-admin users back to home
                resetToHome();
            }
        } catch (error) {
            console.error('Error verifying admin status:', error);
            resetToHome();
        }
    };

    const handleAgreementModalConfirm = () => {
        setShowAgreementModal(false);
        setSettingsActive(true);
    };

    const currencyOptions = [
        { theme: 'bitcoin', code: 'BTC', symbol: 'BTC', image: '/assets/currency-btc.png' },
        { theme: 'kaspa', code: 'KAS', symbol: 'KAS', image: '/assets/currency-kas.png' },
        { theme: 'Alephium', code: 'ALPH', symbol: 'ALPH', image: '/assets/currency-alph.png' },
    ];

    useEffect(() => {
        if (settingsActive) {
            setActiveTab('SETTINGS');
            setPasswordActive(false);
            setAdminPanelActive(false);
        }
    }, [settingsActive]);

    useEffect(() => {
        if (passwordActive) {
            setActiveTab('PASSWORD');
            setSettingsActive(false);
            setAdminPanelActive(false);
        }
    }, [passwordActive]);

    useEffect(() => {
        if (adminPanelActive) {
            // Check if user is admin, if not redirect to home
            if (!isAdmin) {
                resetToHome();
                return;
            }
            setActiveTab('ADMIN');
            setSettingsActive(false);
            setPasswordActive(false);
        }
    }, [adminPanelActive, isAdmin]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const resetToHome = () => {
        setActiveTab('HOME');
        setSettingsActive(false);
        setPasswordActive(false);
        setAdminPanelActive(false);
    };

    const NavigateToPassword = () => {
        setActiveTab('SETTINGS');
    }

    const toggleCoinDropdown = () => {
        setIsDropdownOpen((prev) => !prev);
    };

    const handleCoinSelect = (code: string) => {
        setSelectedCoin(code);
        setIsDropdownOpen(false);
    };

    return (

        <div className="bg-gray-50 h-fit min-h-screen">
            {/* Agreement Modal */}
            {showAgreementModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full">
                        <div className="p-6">
                            <div className="flex items-center mb-4">
                                <div className="bg-yellow-100 rounded-full p-2 mr-3">
                                    <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Hosting Contract Agreement Required
                                </h3>
                            </div>
                            <p className="text-gray-600 mb-6">
                                Please agree to the hosting contract to continue using the platform. You will be redirected to the settings page to review and accept the agreement.
                            </p>
                            <div className="flex justify-end">
                                <button
                                    onClick={handleAgreementModalConfirm}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    Go to Settings
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Switcher />
            <nav className="flex-no-wrap flex w-full items-center justify-between z-10"
                style={{
                    background: 'linear-gradient(90deg, #5E482C 0%, #412F19 100%)'
                }}
            >
                <div className="container mx-auto flex w-full flex-wrap px-3 py-2">
                    <img
                        src="/assets/cashlab_logo.png"
                        className="h-7 md:h-8 my-2 cursor-pointer"
                        alt="Logo"
                        onClick={resetToHome}
                    />
                    <ul className="list-style-none hidden md:flex justify-center items-center flex-grow">
                        {settingsActive ? (
                            <li className="px-4">
                                <button className="transition duration-200 px-4 py-2 text-md border-b"
                                    style={{
                                        borderColor: '#D5C5A9', // Warm Beige border
                                        color: '#D5C5A9', // Warm Beige text
                                        backgroundColor: 'rgba(229, 188, 117, 0.1)', // Golden Tan 10% opacity
                                        borderRadius: '0.375rem', // rounded-md
                                        fontWeight: '700',
                                    }}
                                >
                                    SETTINGS
                                </button>
                            </li>
                        ) : passwordActive ? (
                            <li className="px-4">
                                <button className="transition duration-200 px-4 py-2 text-md border-b"
                                    style={{
                                        borderColor: '#D5C5A9',
                                        color: '#D5C5A9',
                                        backgroundColor: 'rgba(229, 188, 117, 0.1)',
                                        borderRadius: '0.375rem',
                                        fontWeight: '700',
                                    }}
                                >
                                    PASSWORD
                                </button>
                            </li>
                        ) : (
                            ['HOME', 'WORKERS', 'REVENUE'].map((tab) => (
                                <li key={tab} className="px-4">
                                    <button
                                        onClick={() => setActiveTab(tab)}
                                        className="transition duration-200 px-4 py-2 text-md font-bold"
                                        style={{
                                            color: activeTab === tab ? '#D5C5A9' : 'rgba(213, 197, 169, 0.9)', // Warm Beige or slightly transparent
                                            borderBottom: activeTab === tab ? '2px solid #D5C5A9' : 'none',
                                            backgroundColor: activeTab === tab ? 'rgba(229, 188, 117, 0.1)' : 'transparent',
                                            borderRadius: activeTab === tab ? '0.375rem' : '0',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {tab}
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                    <div className='flex ml-auto items-center gap-3'>
                        {isAdmin && (
                            <button
                                onClick={handleAdminPanelClick}
                                className="hidden md:block px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-md"
                                style={{
                                    backgroundColor: '#5E482C',
                                    color: '#D5C5A9',
                                    border: '1px solid #D5C5A9'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#412F19';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#5E482C';
                                }}
                            >
                                Admin Panel
                            </button>
                        )}
                        <CoinDropdown
                            selectedCoin={selectedCoin}
                            currencyOptions={currencyOptions}
                            onSelect={handleCoinSelect}
                        />
                        <UserMenu
                            settingsActive={settingsActive}
                            setSettingsActive={setSettingsActive}
                            passwordActive={passwordActive}
                            setPasswordActive={setPasswordActive}
                        />
                    </div>
                </div>
            </nav>


            <div className="container mx-auto">
                {/* Mobile Admin Panel Button */}
                {isAdmin && (
                    <div className="md:hidden w-full flex justify-center my-2">
                        <button
                            onClick={handleAdminPanelClick}
                            className="px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-md"
                            style={{
                                backgroundColor: '#5E482C',
                                color: '#D5C5A9',
                                border: '1px solid #D5C5A9'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#412F19';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#5E482C';
                            }}
                        >
                            Admin Panel
                        </button>
                    </div>
                )}

                <ul className="list-style-none md:hidden w-full flex items-center justify-center flex-grow my-3 text-xs">
                    {settingsActive ? (
                        <li className="px-4">
                            <button className="bg-gray-100 text-gray-800 py-1 px-2 border-none border-gray-400">
                                 SETTINGS
                            </button>
                        </li>
                    ) : passwordActive ? (
                        <li className="px-4">
                            <button className="bg-gray-100 text-gray-800 py-1 px-2 border-none border-gray-400">
                                PASSWORD
                            </button>
                        </li>
                    ) : adminPanelActive ? (
                        <li className="px-4">
                            <button className="py-1 px-2 border-none rounded"
                                style={{
                                    backgroundColor: 'rgba(94, 72, 44, 0.1)',
                                    color: '#5E482C'
                                }}
                            >
                                ADMIN PANEL
                            </button>
                        </li>
                    ) : (
                        ['HOME', 'WORKERS', 'REVENUE'].map((tab) => (
                            <li key={tab}>
                                <button
                                    onClick={() => setActiveTab(tab)}
                                    className={`text-gray-800 py-1 px-2 border-gray-400  ${activeTab === tab
                                        ? 'text-blue-950 border-b border-blue-900'
                                        : 'bg-white'
                                        }`}
                                >
                                    {tab}
                                </button>
                            </li>
                        ))
                    )}
                </ul>
                {activeTab === 'HOME' && <HomeData NavigateToPassword={NavigateToPassword} />}
                {activeTab === 'WORKERS' && <Workers />}
                {activeTab === 'REVENUE' && <RevenuePage />}
                {activeTab === 'SETTINGS' && <Settings activeTabExit={resetToHome} />}
                {activeTab === 'PASSWORD' && <Password activeTabExit={resetToHome} />}
                {activeTab === 'ADMIN' && <AdminPanel activeTabExit={resetToHome} />}
            </div>
        </div>
    );
}

export default WorkerData;
